import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Project: Real-Time Chat (Socket.io)
//  Chapter 10 · Projects
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `// components/ChatRoom.jsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || 'http://localhost:3001';

export default function ChatRoom({ roomId, username }) {
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [typingUsers, setTyping]  = useState([]);
  const [onlineUsers, setOnline]  = useState([]);
  const socketRef = useRef(null);
  const bottomRef = useRef(null);
  const typingTimeout = useRef(null);

  // Connect to socket
  useEffect(() => {
    const socket = io(SOCKET_URL, { auth: { username } });
    socketRef.current = socket;

    socket.emit('join', { roomId, username });

    socket.on('message',     msg  => setMessages(prev => [...prev, msg]));
    socket.on('userTyping',  list => setTyping(list.filter(u => u !== username)));
    socket.on('onlineUsers', list => setOnline(list));
    socket.on('history',     msgs => setMessages(msgs));

    return () => socket.disconnect();
  }, [roomId, username]);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = useCallback(() => {
    if (!input.trim()) return;
    socketRef.current.emit('message', {
      roomId, text: input.trim(), username,
      timestamp: new Date().toISOString(),
    });
    setInput('');
    socketRef.current.emit('stopTyping', { roomId, username });
  }, [input, roomId, username]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    socketRef.current.emit('typing', { roomId, username });
    clearTimeout(typingTimeout.current);
    typingTimeout.current = setTimeout(() => {
      socketRef.current.emit('stopTyping', { roomId, username });
    }, 1500);
  };

  return (
    <div className="chat-room">
      <aside className="online-panel">
        <h4>Online ({onlineUsers.length})</h4>
        {onlineUsers.map(u => (
          <div key={u} className="online-user">
            <span className="dot" />
            {u}
          </div>
        ))}
      </aside>

      <main className="chat-main">
        <div className="messages">
          {messages.map((msg, i) => (
            <div key={i} className={\`message \${msg.username === username ? 'mine' : 'theirs'}\`}>
              <span className="sender">{msg.username}</span>
              <p>{msg.text}</p>
              <span className="time">
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          ))}
          {typingUsers.length > 0 && (
            <div className="typing">
              {typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing...
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className="input-area">
          <input
            value={input}
            onChange={handleInputChange}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            placeholder="Type a message..."
          />
          <button onClick={sendMessage}>Send</button>
        </div>
      </main>
    </div>
  );
}`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ProjectRealTimeChatSocketIO() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 10 · Projects</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Project: Real-Time Chat (Socket.io)</h1>
        <p style={styles.desc}>Build a real-time multi-room chat application using React and Socket.io.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Project Overview ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Project Overview</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "A real-time chat application:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Multiple chat rooms" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Real-time messaging (Socket.io)" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Online users list with avatars" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Typing indicators (&#39;Alice is typing...&#39;)" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Message timestamps" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Sound notifications" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Persistent username (localStorage)" }} />
      </div>

      /* ── Chat Component ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Chat Component</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── Extension Challenges ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Extension Challenges</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 EXTENSION CHALLENGES</div>
          <div style={styles.qItem}>✦ 1. Add image and file sharing</div>
          <div style={styles.qItem}>✦ 2. Add emoji picker</div>
          <div style={styles.qItem}>✦ 3. Add message reactions (👍 ❤️ 😂)</div>
          <div style={styles.qItem}>✦ 4. Add private DM (direct message) rooms</div>
          <div style={styles.qItem}>✦ 5. Add read receipts (✓ / ✓✓)</div>
          <div style={styles.qItem}>✦ 6. Add message search within a room</div>
          <div style={styles.qItem}>✦ 7. Deploy with a Node.js + Socket.io backend on Render or Railway</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
